/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import axios from 'axios';
import swal from 'sweetalert2';
import { useNavigate } from 'react-router-dom';
import { useUserState } from '../contexts/UserContext';
import Loading from '../components/common/Loading';
import Pagination from '../components/common/Pagination';
import OrderDetailModal from '../components/mypage/OrderDetailModal';

const Container = styled.div`
  display: flex;
  flex-direction: row;
  width: 100%;
  padding-top: 30px;
  @media (max-width: 768px) {
    flex-direction: column;
    padding: 20px 16px;
  }
`;

const Left = styled.div`
  width: 30%;
  height: 240px;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  padding-top: 40px;
  @media (max-width: 768px) {
    width: 100%;
    height: auto;
    padding-top: 0;
    margin-bottom: 24px;
    gap: 12px;
  }
`;

const Welcome = styled.div`
  font-weight: 700;
  font-size: 30px;
  margin-right: 20px;
  @media (max-width: 768px) {
    font-size: 22px;
  }
`;

const Text = styled.div`
  font-weight: 500;
  font-size: 18px;
  margin-bottom: 5px;
  @media (max-width: 768px) {
    font-size: 15px;
  }
`;

const Box = styled.div`
  width: 70%;
  min-height: 70vh;
  box-shadow: 0px 0px 50px 20px rgba(138, 138, 138, 0.1);
  border-radius: 50px;
  padding: 20px 40px;
  margin-bottom: 30px;
  @media (max-width: 768px) {
    width: 100%;
    border-radius: 20px;
    padding: 16px;
    min-height: auto;
    box-shadow: 0px 0px 20px 5px rgba(138, 138, 138, 0.08);
  }
`;

const BtnGroup = styled.div`
  display: flex;
  gap: 8px;
  @media (max-width: 768px) {
    flex-direction: row;
  }
`;

const ActionBtn = styled.button`
  padding: 8px 20px;
  border: 2px solid black;
  border-radius: 8px;
  background: white;
  font-size: 15px;
  font-weight: 600;
  cursor: pointer;
  &:hover { background: #f5f5f5; }
`;

const OrderTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  td, th {
    text-align: center;
    padding: 12px 10px;
    border: none;
    font-size: 14px;
  }
  tbody tr {
    height: 50px;
    cursor: pointer;
    transition: background 0.1s;
    &:hover { background: #f8f9fa; }
  }
  thead > tr {
    background-color: #eee;
    height: 50px;
  }
  @media (max-width: 768px) {
    display: none;
  }
`;

const MobileCard = styled.div`
  display: none;
  @media (max-width: 768px) {
    display: flex;
    flex-direction: column;
    gap: 10px;
  }
`;

const Card = styled.div`
  padding: 14px 16px;
  border: 1px solid #eee;
  border-radius: 12px;
  cursor: pointer;
  transition: background 0.1s;
  &:hover { background: #f8f9fa; }
`;

const CardRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 14px;
  & + & { margin-top: 6px; }
`;

const CardLabel = styled.span`
  color: #888;
  font-size: 13px;
`;

const CardValue = styled.span`
  font-weight: 600;
`;

const PriceText = styled.span`
  color: #333;
  font-weight: 700;
  font-size: 15px;
`;

const CancelBadge = styled.span`
  display: inline-block;
  padding: 2px 8px;
  border-radius: 6px;
  font-size: 12px;
  font-weight: 700;
  background: #fef2f2;
  color: #dc2626;
`;

const EmptyState = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 200px;
  color: #999;
  font-size: 15px;
`;

const MyPage = () => {
  const navigate = useNavigate();
  const { user } = useUserState();
  const [orders, setOrders] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const postsPerPage = 10;

  useEffect(() => {
    fetchOrders();
  }, [user]);

  const fetchOrders = async () => {
    try {
      setIsLoading(true);
      const { data } = await axios.get(`/api/orders/orderlist/${user._id}`);
      // data: { data: [...], id: [...] }
      const merged = data.data.map((item, i) => ({
        ...item,
        _id: data.id[i].id,
      }));
      setOrders(merged);
    } catch (e) {
      if (e.response?.status !== 404) console.error(e);
      setOrders([]);
    } finally {
      setIsLoading(false);
    }
  };

  const indexOfLast = currentPage * postsPerPage;
  const indexOfFirst = indexOfLast - postsPerPage;
  const currentOrders = orders ? orders.slice(indexOfFirst, indexOfLast) : [];

  const formatPrice = (price) => {
    if (!price) return '-';
    return price.toLocaleString('ko-KR') + '원';
  };

  const _handleLogout = () => {
    swal.fire({
      text: '로그아웃 하시겠습니까?',
      icon: 'question',
      showCancelButton: true,
      confirmButtonColor: '#7499ab',
      cancelButtonColor: '#a8a8a8',
      confirmButtonText: '확인',
      cancelButtonText: '취소',
    }).then(async (result) => {
      if (result.isConfirmed) {
        localStorage.removeItem('user');
        navigate('/');
        window.location.reload();
      }
    });
  };

  const _handlePassword = async () => {
    const { value: pwd } = await swal.fire({
      title: '새로운 비밀번호를 입력해 주세요.',
      text: '변경 후에는 새로운 비밀번호로 재로그인이 필요합니다.',
      input: 'password',
      inputPlaceholder: '6자리 이상 입력',
      confirmButtonText: '확인',
      cancelButtonText: '취소',
      confirmButtonColor: '#232323',
      cancelButtonColor: '#a3a3a3',
      showCancelButton: true,
      inputValidator: (value) => {
        if (!value) return '입력된 값이 없습니다.';
      },
    });
    if (pwd) {
      try {
        await axios.patch(`/api/users/password/${user._id}`, { user_password: pwd });
      } catch (err) {
        if (err.response?.status === 400) {
          swal.fire({ text: '오류가 발생했습니다.', icon: 'error', showConfirmButton: false, timer: 1000 });
          return;
        }
      }
      swal.fire({
        title: '변경이 완료되었습니다.',
        text: '재로그인을 위해 로그아웃됩니다.',
        icon: 'success',
        showConfirmButton: true,
        confirmButtonColor: '#232323',
      }).then((result) => {
        if (result.isConfirmed) {
          localStorage.removeItem('user');
          navigate('/');
          window.location.reload();
        }
      });
    }
  };

  return (
    <>
      <Container>
        <Left>
          <div>
            <Welcome>
              {user.manager_name} 님, <br />
              환영합니다.
            </Welcome>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
            <Text>{user.company_name}</Text>
            <Text>{user.user_email}</Text>
            <Text>{user.manager_contact}</Text>
          </div>
          <BtnGroup>
            <ActionBtn onClick={_handleLogout}>로그아웃</ActionBtn>
            <ActionBtn onClick={_handlePassword}>비밀번호 변경</ActionBtn>
          </BtnGroup>
        </Left>
        <Box>
          <h2>발주 내역</h2>
          {!orders || orders.length === 0 ? (
            <EmptyState>
              {isLoading ? '' : '발주 내역이 없습니다.'}
            </EmptyState>
          ) : (
            <>
              {/* 데스크톱 테이블 */}
              <OrderTable>
                <thead>
                  <tr>
                    <th>발주번호</th>
                    <th>발주일</th>
                    <th>운송장 번호</th>
                    <th>금액</th>
                  </tr>
                </thead>
                <tbody>
                  {currentOrders.map((order, i) => (
                    <tr key={i} onClick={() => setSelectedOrder(order)} style={order.deleted ? { opacity: 0.5 } : undefined}>
                      <td>{order.orderId}</td>
                      <td>{order.time}</td>
                      <td>{order.deleted ? <CancelBadge>취소됨</CancelBadge> : order.postNum}</td>
                      <td>{order.orderId ? formatPrice(parseInt(order.orderId.split('_')[2], 10)) : '-'}</td>
                    </tr>
                  ))}
                </tbody>
              </OrderTable>

              {/* 모바일 카드 */}
              <MobileCard>
                {currentOrders.map((order, i) => (
                  <Card key={i} onClick={() => setSelectedOrder(order)} style={order.deleted ? { opacity: 0.5 } : undefined}>
                    <CardRow>
                      <CardValue>{order.orderId}</CardValue>
                      <PriceText>{order.orderId ? formatPrice(parseInt(order.orderId.split('_')[2], 10)) : ''}</PriceText>
                    </CardRow>
                    <CardRow>
                      <CardLabel>{order.time}</CardLabel>
                      <CardLabel>{order.deleted ? <CancelBadge>취소됨</CancelBadge> : order.postNum}</CardLabel>
                    </CardRow>
                  </Card>
                ))}
              </MobileCard>

              <Pagination
                postsPerPage={postsPerPage}
                totalPosts={orders.length}
                paginate={setCurrentPage}
              />
            </>
          )}
        </Box>
      </Container>

      {selectedOrder && (
        <OrderDetailModal
          userId={user._id}
          orderId={selectedOrder._id}
          orderIdStr={selectedOrder.orderId}
          onClose={() => setSelectedOrder(null)}
        />
      )}

      {isLoading && <Loading />}
    </>
  );
};

export default MyPage;
